import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { FirebaseApp, getApps, initializeApp } from 'firebase/app';
import { Auth as FirebaseAuth, getAuth as getFirebaseAuth, signInWithCustomToken as signInWithCustomTokenFirebase, signOut as signOutFirebase } from 'firebase/auth';
import {
  Firestore as FirebaseFirestore,
  initializeFirestore as initializeFirebaseFirestore,
  getFirestore as getFirebaseFirestore,
  CACHE_SIZE_UNLIMITED,
  persistentLocalCache,
  persistentMultipleTabManager,
  DocumentReference,
  doc as firebaseDoc,
  docData,
  updateDoc,
  CollectionReference,
  collection as firebaseCollection,
  collectionData,
  Query
} from '@angular/fire/firestore';
import { Observable } from 'rxjs';

/**
 * Lightweight Firestore wrapper for the secondary Shopify Firebase project.
 * Uses the Firebase JS SDK directly to avoid interfering with AngularFire's default app providers.
 */
@Injectable({ providedIn: 'root' })
export class ShopifyFirestoreService {

  private static readonly SHOPIFY_APP_NAME = 'shopify';
  private firebaseApp!: FirebaseApp;
  private firestore!: FirebaseFirestore;
  private auth!: FirebaseAuth;

  constructor() {
    this.initializeShopifyFirebase();
  }

  private initializeShopifyFirebase(): void {
    const apps = getApps();
    const existing = apps.find(a => a.name === ShopifyFirestoreService.SHOPIFY_APP_NAME);
    if (existing) {
      this.firebaseApp = existing;
    } else {
      this.firebaseApp = initializeApp(environment.shopifyFirebase as any, ShopifyFirestoreService.SHOPIFY_APP_NAME);
    }

    // Ensure Firestore is initialized with same cache settings as the default app
    try {
      initializeFirebaseFirestore(this.firebaseApp, {
        ignoreUndefinedProperties: true,
        localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager(), cacheSizeBytes: CACHE_SIZE_UNLIMITED })
      });
    } catch {
      // initializeFirestore throws if already initialized; ignore
    }
    this.firestore = getFirebaseFirestore(this.firebaseApp);
    this.auth = getFirebaseAuth(this.firebaseApp);
  }

  doc<T>(path: string): DocumentReference<T> {
    return firebaseDoc(this.firestore, path) as DocumentReference<T>;
  }

  col<T>(path: string): CollectionReference<T> {
    return firebaseCollection(this.firestore, path) as CollectionReference<T>;
  }

  docValueChanges$<T>(path: string): Observable<T | undefined> {
    const ref = this.doc<T | undefined>(path);
    return docData<T | undefined>(ref);
  }

  colValueChanges$<T>(queryRef: Query<T>): Observable<T[]> {
    return collectionData<T>(queryRef);
  }

  async updateDoc(path: string, data: any): Promise<void> {
    const ref = this.doc(path);
    await updateDoc(ref, data);
  }

  async signIn(customToken: string): Promise<void> {
    await signInWithCustomTokenFirebase(this.auth, customToken);
  }

  async signOut(): Promise<void> {
    await signOutFirebase(this.auth);
  }
}